import { type QueryHookOptions } from "@apollo/client";
import {
  type VaultDocumentByRefQuery,
  type VaultDocumentByRefQueryVariables,
  type VaultDocumentsInput,
} from "#graphql/supergraphTypes";
import {
  useVaultDocumentByRef,
  type UseVaultDocumentByRefReturn,
} from "./useVaultDocumentByRef";

export interface UseVaultDocumentOptions extends Omit<
  QueryHookOptions<VaultDocumentByRefQuery, VaultDocumentByRefQueryVariables>,
  "variables"
> {
  /**
   * @deprecated No longer used. `useVaultDocument` fetches by `publicRefId` via
   * `vaultDocumentByRef` rather than scanning a `vaultDocumentsSearch` page, so
   * there is nothing left to narrow. Accepted (and ignored) so callers built
   * against the pre-DV-577 scan-based hook keep compiling; will be removed in a
   * future major version.
   */
  input?: Omit<VaultDocumentsInput, "after" | "first">;
  /** @deprecated No longer used — see `input`. */
  first?: number;
}

export type UseVaultDocumentReturn = UseVaultDocumentByRefReturn;

/**
 * DV-345 — document-detail route: fetch a single Vault document by `id`.
 *
 * `VaultDocument` is keyed by `publicRefId` (which the presentational
 * `VaultDocument.id` maps from), so the `id` arg here is a `publicRefId`. Thin
 * wrapper over `useVaultDocumentByRef`, which also carries the document's full
 * version history (`node.versions`) — the caller maps it with
 * `apiVaultDocumentToVaultDocument(node, lookups)`.
 *
 * @example
 * const { node, loading } = useVaultDocument(documentId);
 */
export const useVaultDocument = (
  id: string,
  options?: UseVaultDocumentOptions,
): UseVaultDocumentReturn => {
  // Strip the deprecated `input`/`first` fields rather than spreading them into
  // Apollo's query options — see `UseVaultDocumentOptions`.
  const { input: _input, first: _first, ...queryOptions } = options ?? {};
  return useVaultDocumentByRef(id, queryOptions);
};
